"use client";

import React, { ReactNode } from "react";
import Link from "next/link";
import { useInfiniteLGSCards } from "modules/lgs/hooks/useInfiniteLGSCards";
import { useLGSUnifiedFilters } from "modules/lgs/hooks/useLGSUnifiedFilters";
import TradeSectionTitle from "modules/lgs/components/stores/table/TradeSectionTitle";
import LGSTradesItemsView from "modules/lgs/components/stores/table/LGSTradesItemsView";
import { EmptyStateCard } from "modules/common/components/EmptyStateCard";
import { useQuickJoinStore } from "modules/lgs/hooks/useQuickJoinStore";
import { ArrowRight, Package, Search } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { cn } from "utils/utils";
import { ViewIndex } from "modules/common/model/viewTypes";
import { LGSFacetSidebar } from "../facets/LGSFacetSidebar";
import { TradeListType } from "modules/tradeLists/model/tradeList";
import SidebarPageLayout from "modules/common/components/SidebarPageLayout";
import { VisitorFunnelCard } from "modules/common/components/funnels/VisitorFunnelCard";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { useLGSCardFacets } from "modules/lgs/hooks/useLGSCardFacets";

interface LGSCardsSectionClientProps {
  storeId: number;
  type: "trades" | "wants";
  viewAllLink?: string;
  /** Optional mobile filter bar to display instead of title on mobile */
  mobileFilterBar?: ReactNode;
  /** When true, removes the card wrapper styling on mobile */
  hideCard?: boolean;
  maxItems?: number;
  /** Hide the section title (useful on dedicated pages that have their own heading) */
  hideTitle?: boolean;
  /** Short description displayed below the title */
  description?: string;
  /** View index from SSR cookie — eliminates flash by rendering correct view immediately */
  initialViewIndex?: ViewIndex;
  /** Store name for smart join CTA */
  storeName?: string;
  /** Store coordinates [lng, lat] for centering the store selector dialog */
  storeCoords?: [number, number];
  /** When true, the count is rendered by the page's mainbar, so the desktop
   *  section-title row collapses. The row still renders on mobile, where it
   *  hosts the mobile filter bar. */
  countInMainbar?: boolean;
}

const sectionConfig = {
  trades: {
    title: "Haves",
    emptyMessage: "No cards for trade have been added to this store yet.",
    signedOutMessage: "Sign in to see cards available for trade at this store.",
    signedInMessage: "Be the first to list cards for trade at this store.",
    ctaText: "Create List",
    icon: Package,
    filterKey: "tradesFilters" as const,
    listType: TradeListType.TRADES,
  },
  wants: {
    title: "Wants",
    emptyMessage: "No wanted cards have been added to this store yet.",
    signedOutMessage:
      "Sign in to see cards players are looking for at this store.",
    signedInMessage:
      "Be the first to post cards you're looking for at this store.",
    ctaText: "Create Wants List",
    icon: Search,
    filterKey: "wantsFilters" as const,
    listType: TradeListType.WANTS,
  },
} as const;

export function LGSCardsSectionClient({
  storeId,
  type,
  viewAllLink,
  mobileFilterBar,
  hideCard,
  maxItems,
  hideTitle,
  description,
  initialViewIndex,
  storeName,
  storeCoords,
  countInMainbar,
}: LGSCardsSectionClientProps) {
  const config = sectionConfig[type];
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Use the SAME unified filters that LGSHeaderTabs controls
  const {
    [config.filterKey]: lgsCardsReq,
    activeView,
    hasActiveFilters,
  } = useLGSUnifiedFilters(storeId, "", initialViewIndex);

  // When maxItems is set, reduce perPage to match so the backend doesn't
  // fetch more items than we display. The query key must match the server prefetch.
  const effectiveReq = maxItems
    ? { ...lgsCardsReq, perPage: maxItems }
    : lgsCardsReq;
  const { resultTotal, isFetching: areFacetsFetching } = useLGSCardFacets(
    lgsCardsReq,
    { enabled: !maxItems }
  );

  // Use the existing infinite scroll hook that responds to filter changes
  // React Query will use prefetched data from HydrationBoundary as initial data
  const {
    data: dataResults,
    totalResults,
    currentCount,
    isFetching,
    isPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteLGSCards(effectiveReq, {
    // Compact sections only exist in the CSS-hidden desktop overview tree.
    // Dedicated pages have no maxItems limit and remain enabled at every size.
    enabled: !maxItems || isDesktop,
  });

  // Helper function to check if data is empty (same as original LGSContent)
  const isDataEmpty = (data: unknown) => {
    if (!data || typeof data !== "object") return true;
    const dataObj = data as { pages?: Array<{ items?: unknown[] }> };
    if (!dataObj.pages || dataObj.pages.length === 0) return true;
    const firstPage = dataObj.pages[0];
    return !firstPage || !firstPage.items || firstPage.items.length === 0;
  };

  // Smart CTA: check membership for join button
  const { isMember, joinStore, isJoining } = useQuickJoinStore(storeId, {
    storeCoords,
  });

  const hasData =
    !!dataResults?.pages?.some((p) => p.items.length > 0) || isPending;
  const total = maxItems
    ? (dataResults?.pages[0]?.total ?? totalResults ?? 0)
    : (resultTotal ?? 0);

  const Icon = config.icon;

  // Determine empty state message based on filter state
  const emptyDescription = hasActiveFilters
    ? "No cards match your current filters. Try adjusting your search or filters."
    : config.emptyMessage;

  // Embedded / maxItems mode: no sidebar
  if (maxItems) {
    return (
      <div className="flex h-full flex-col">
        <TradeSectionTitle
          currentCount={currentCount}
          total={total}
          title={hideTitle ? undefined : config.title}
          description={description}
          type={type}
          viewAllLink={maxItems && total > maxItems ? viewAllLink : undefined}
          leftContent={mobileFilterBar}
          isLoading={isPending}
          sticky={!!maxItems}
        />
        <div
          className={cn(
            "flex-1 overflow-hidden",
            !hasData && "flex items-center justify-center"
          )}
        >
          {hasData ? (
            <LGSTradesItemsView
              data={dataResults}
              isPending={isPending}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              isMatchedTrades={false}
              activeView={activeView}
              maxItems={maxItems}
            />
          ) : (
            <EmptyStateCard
              variant="compact"
              icon={<Icon className="text-primary h-8 w-8" />}
              title={hasActiveFilters ? "No Results" : config.emptyMessage}
              description={emptyDescription}
              ctaText={
                hasActiveFilters
                  ? undefined
                  : !isMember && storeName
                    ? `Join ${storeName}`
                    : config.ctaText
              }
              ctaLink={
                hasActiveFilters || (!isMember && storeName)
                  ? undefined
                  : "/x/lists"
              }
              onCtaClick={
                !hasActiveFilters && !isMember && storeName
                  ? () => joinStore()
                  : undefined
              }
              ctaLoading={isJoining}
            />
          )}
        </div>
        {maxItems && viewAllLink && (
          <div className="flex justify-center pb-1 pt-3">
            <Link
              href={viewAllLink}
              className={cn(
                "btn btn-soft btn-sm btn-primary gap-1",
                total <= maxItems && "pointer-events-none opacity-0"
              )}
              tabIndex={total <= maxItems ? -1 : undefined}
              aria-hidden={total <= maxItems || undefined}
            >
              View all {total} cards
              <ArrowRight size={ICON_SIZE} />
            </Link>
          </div>
        )}
      </div>
    );
  }

  // Full page mode: sidebar + content
  return (
    <SidebarPageLayout
      fullHeight
      sidebarHeader={
        storeName ? (
          <VisitorFunnelCard
            context={{
              kind: "store",
              direction: type === "wants" ? "wants" : "haves",
              storeName,
            }}
          />
        ) : null
      }
      sidebar={
        <LGSFacetSidebar
          storeId={storeId}
          listType={config.listType}
          facetFilters={effectiveReq}
        />
      }
    >
      <div className="flex h-full flex-col">
        <TradeSectionTitle
          currentCount={currentCount}
          total={total}
          title={hideTitle ? undefined : config.title}
          description={description}
          type={type}
          viewAllLink={viewAllLink}
          leftContent={mobileFilterBar}
          isLoading={isPending || areFacetsFetching}
          countInMainbar={countInMainbar}
        />
        <div
          className={cn(
            "flex-1 overflow-hidden",
            !hasData && "flex items-center justify-center"
          )}
        >
          {hasData ? (
            <LGSTradesItemsView
              data={dataResults}
              isPending={isPending}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              isMatchedTrades={false}
              activeView={activeView}
            />
          ) : (
            <EmptyStateCard
              variant="compact"
              icon={<Icon className="text-primary h-8 w-8" />}
              title={hasActiveFilters ? "No Results" : config.emptyMessage}
              description={emptyDescription}
              ctaText={
                hasActiveFilters
                  ? undefined
                  : !isMember && storeName
                    ? `Join ${storeName}`
                    : config.ctaText
              }
              ctaLink={
                hasActiveFilters || (!isMember && storeName)
                  ? undefined
                  : "/x/lists"
              }
              onCtaClick={
                !hasActiveFilters && !isMember && storeName
                  ? () => joinStore()
                  : undefined
              }
              ctaLoading={isJoining}
            />
          )}
        </div>
      </div>
    </SidebarPageLayout>
  );
}
